import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, ZoomIn, ZoomOut } from "lucide-react";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../utils/canvasUtils";

interface ImageUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (image: string) => Promise<void>;
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({ isOpen, onClose, onUpload }) => {
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Cropper State
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setSelectedImage(e.target?.result as string);
                setZoom(1);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setSelectedImage(ev.target?.result as string);
                setZoom(1);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedImage || !croppedAreaPixels) return;
        setIsLoading(true);
        try {
            const croppedImage = await getCroppedImg(selectedImage, croppedAreaPixels);
            if (croppedImage) {
                await onUpload(croppedImage);
                onClose();
                // Reset state
                setSelectedImage(null);
                setZoom(1);
            }
        } catch (error) {
            console.error("Upload failed", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        {...({ className: "absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto", onClick: onClose } as any)}
                    />
                    <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 50, opacity: 0 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        {...({ className: "bg-white rounded-2xl p-5 w-full max-w-lg border border-gray-100 shadow-xl max-h-[90vh] overflow-hidden pointer-events-auto z-10 flex flex-col" } as any)}
                    >
                        <div className="flex items-start justify-between mb-4 flex-shrink-0">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Modifier la photo de profil
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Ajustez et recadrez votre image
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {selectedImage ? (
                            <div className="flex-1 flex flex-col min-h-[300px]">
                                <div className="relative w-full h-[300px] bg-gray-900 rounded-xl overflow-hidden mb-4">
                                    <Cropper
                                        image={selectedImage}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={1}
                                        onCropChange={setCrop}
                                        onCropComplete={onCropComplete}
                                        onZoomChange={setZoom}
                                        cropShape="round"
                                        showGrid={false}
                                    />
                                </div>
                                <div className="flex items-center gap-4 px-2 mb-4">
                                    <ZoomOut size={16} className="text-gray-500" />
                                    <input
                                        type="range"
                                        value={zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        aria-labelledby="Zoom"
                                        onChange={(e) => setZoom(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                    <ZoomIn size={16} className="text-gray-500" />
                                </div>
                                <div className="flex justify-center mb-4">
                                    <button
                                        onClick={() => setSelectedImage(null)}
                                        className="text-sm text-red-500 font-medium hover:underline"
                                    >
                                        Changer d'image
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-200 bg-gray-50/60 rounded-xl mb-4">
                                <label
                                    htmlFor="imageInput"
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                    }}
                                    onDragLeave={() => {}}
                                    onDrop={handleDrop}
                                    className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
                                >
                                    <div className="w-20 h-20 rounded-full bg-white border border-gray-200 flex items-center justify-center mb-4 shadow-sm">
                                        <Camera size={32} className="text-blue-500" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-800 mb-1">
                                        Cliquez ou glissez une image ici
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        PNG, JPG jusqu'à 5 Mo
                                    </p>
                                </label>
                                <input
                                    id="imageInput"
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </div>
                        )}

                        <div className="flex gap-3 flex-shrink-0">
                            <button
                                onClick={onClose}
                                className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={!selectedImage || isLoading}
                                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-white transition-colors ${!selectedImage || isLoading
                                    ? "bg-gray-300 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                            >
                                {isLoading ? "Enregistrement..." : "Enregistrer la photo"}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
